"use client";

import React, { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Leaf, ShieldCheck, Clock } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.15,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const servicesData = [{
  title: "Probiotic Fizz Formulation",
  description: "Engineered for gut health and vibrant flavor profiles. We specialize in stable probiotic integration for carbonated RTDs that maintain efficacy throughout their shelf life.",
  badge: "Bestseller",
  badgeVariant: "default" as const,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/probiotic_fizz_can.png",
  process: "Our R&D team utilizes proprietary cold-fill techniques to preserve live cultures while ensuring a crisp, refreshing carbonation level of 2.5 to 3.5 volumes.",
  frequency: "MOQ: 5,000 cans per SKU. Typical lead time is 4-6 weeks.",
  benefits: "High consumer demand for functional wellness, extended 12-month shelf life, and clean-label ingredient profiles."
}, {
  title: "Adaptogen & Nootropic Seltzers",
  description: "Crafting the next generation of 'smart' beverages. We blend botanical extracts like Ashwagandha and Lion's Mane into sleek, low-calorie sparkling water bases.",
  badge: "Trending",
  badgeVariant: "secondary" as const,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/adaptogen_seltzer_production.png",
  process: "Precision extraction and emulsification ensure that fat-soluble botanicals remain perfectly suspended and bioavailable in every sip.",
  frequency: "Custom small-batch runs available for market testing phases.",
  benefits: "Taps into the 'sober-curious' and cognitive-performance markets with zero-sugar, high-efficacy formulations."
}, {
  title: "Electrolyte Recovery Drinks",
  description: "High-performance hydration solutions designed for rapid absorption. We balance mineral salts and natural flavors for a premium post-workout experience.",
  badge: "Technical",
  badgeVariant: "outline" as const,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/electrolyte_recovery_beverage.png",
  process: "Ion-balanced mixing followed by counter-pressure canning to prevent oxidation and preserve the delicate mineral profile.",
  frequency: "Continuous production contracts available for high-volume brands.",
  benefits: "Optimized for fitness enthusiasts, featuring essential magnesium, potassium, and sodium ratios with zero synthetic dyes."
}, {
  title: "Precision Counter-Pressure Canning",
  description: "The gold standard for carbonated RTDs. Our facility utilizes advanced nitrogen dosing and counter-pressure technology to ensure maximum product integrity.",
  badge: "Core Service",
  badgeVariant: "default" as const,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1607036/img/canning_line_sleek.png",
  process: "Sleek 12oz and 16oz can capabilities with automated shrink-sleeve labeling and rigorous dissolved oxygen (DO) testing.",
  frequency: "Daily production capacity of 50,000 units with 24/7 quality monitoring.",
  benefits: "Ultra-low oxygen pickup for superior flavor retention and professional retail-ready packaging that stands out on shelves."
}];
function ServiceBlock({
  service,
  index
}: {
  service: (typeof servicesData)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="114912" data-index={index} ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className={`flex flex-col space-y-8 ${isEven ? "lg:order-1" : "lg:order-2"}`}>
        <div className="space-y-4">
          {service.badge && <Badge variant={service.badgeVariant} className="mb-2 bg-primary text-primary-foreground font-mono uppercase tracking-widest text-[10px] rounded-full px-3 py-1">
              {service.badge}
            </Badge>}
          <h3 className="text-3xl md:text-4xl font-sans font-semibold text-foreground tracking-tighter">
            {service.title}
          </h3>
          <p className="text-lg text-muted-foreground leading-relaxed">
            {service.description}
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full border-t border-border/40">
          <AccordionItem value="process" className="border-border/40">
            <AccordionTrigger className="text-base font-medium hover:text-primary transition-colors py-4">
              <span className="flex items-center gap-3 font-mono text-sm uppercase tracking-wide">
                <Leaf className="size-4 text-primary" />
                Technical Process
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
              {service.process}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="frequency" className="border-border/40">
            <AccordionTrigger className="text-base font-medium hover:text-primary transition-colors py-4">
              <span className="flex items-center gap-3 font-mono text-sm uppercase tracking-wide">
                <Clock className="size-4 text-primary" />
                Production Specs
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
              {service.frequency}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="benefits" className="border-border/40">
            <AccordionTrigger className="text-base font-medium hover:text-primary transition-colors py-4">
              <span className="flex items-center gap-3 font-mono text-sm uppercase tracking-wide">
                <ShieldCheck className="size-4 text-primary" />
                Market Advantages
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-muted-foreground leading-relaxed pb-4">
              {service.benefits}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {}
      <div className={`relative h-[400px] md:h-[500px] lg:h-[600px] w-full group ${isEven ? "lg:order-2" : "lg:order-1"}`}>
        {}
        <div className={`absolute inset-0 bg-primary/10 transition-transform duration-1000 ease-out backdrop-blur-sm ${isVisible ? "translate-x-4 translate-y-4" : "translate-x-0 translate-y-0"} ${isEven ? "rounded-tr-[3rem] rounded-bl-[3rem]" : "rounded-tl-[3rem] rounded-br-[3rem]"}`} />
        
        {}
        <div className={`absolute inset-0 overflow-hidden bg-muted shadow-xl transition-transform duration-1000 ease-out ${isVisible ? "scale-100" : "scale-95"} ${isEven ? "rounded-tr-[3rem] rounded-bl-[3rem] rounded-tl-lg rounded-br-lg" : "rounded-tl-[3rem] rounded-br-[3rem] rounded-tr-lg rounded-bl-lg"}`}>
          <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 to-transparent pointer-events-none" />
        </div>
      </div>
    </div>;
}
export default function ExpertiseForEveryAcre() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="manufacturing-solutions" className="py-24 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 md:mb-32 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4 font-mono">
            Technical Capabilities
          </h2>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-sans font-semibold text-foreground mb-6 leading-tight tracking-tighter">RTD Beverage Contract Manufacturing Services</h1>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            From custom formulation to counter-pressure canning, we provide the technical precision required to bring your functional beverage products to life — from formulation through RTD canning.
          </p>
        </div>

        {}
        <div className="space-y-32 md:space-y-48">
          {servicesData.map((service, index) => <ServiceBlock key={index} service={service} index={index} />)}
        </div>
      </div>
    </section>;
}